import React, { useCallback, useState, useEffect, useRef } from "react";
import { FaArrowLeft, FaPen, FaCheck, FaPlus, FaTrash, FaGripVertical, FaChevronDown, FaChevronRight, FaFilePdf, FaTimes, FaCog } from "react-icons/fa";
import "../styles/EventDetail.css";
import { apiFetch } from "../utils/api";
import { formatDateWithWeekday } from "../utils/dateFormat";
import { getMarkColorStyle } from "../utils/markColors";
import { handleTableInputKeyDown } from "../utils/tableNavigation";
import { MarkColorBadge, MarkColorPicker } from "./MarkColorPicker";
import { useNotifications } from "./NotificationProvider";

const createDraftId = () => `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
const createEmptyPriceRow = () => ({
  _draftId: createDraftId(),
  getraenkeart: "",
  markenfarbe: "",
  festpreis: "",
  bedienpreis: "",
});
const createEmptyPfandRow = () => ({
  _draftId: createDraftId(),
  artikel: "",
  farbe: "",
  betrag: "",
});
const createEmptyMarkenRow = () => ({
  _draftId: createDraftId(),
  name: "",
  wert: "",
});
const createEmptyCustomBillingRow = () => ({
  _draftId: createDraftId(),
  position_type: "custom",
  custom_type: "manual",
  source_id: null,
  label: "",
  farbe: "",
  anzahl: "",
  zurueck: "",
  preis: 0,
  betrag: "",
});
const createEmptyDeliveryBillingRow = (deliveryNoteKey) => ({
  _draftId: createDraftId(),
  position_type: "custom",
  custom_type: "delivery_note",
  delivery_note_key: deliveryNoteKey,
  source_id: null,
  label: "",
  farbe: "",
  anzahl: "",
  zurueck: null,
  preis: "",
  betrag: 0,
});

const parseCurrencyInput = (value) => {
  const raw = String(value ?? "").trim().replace(/\s/g, "").replace("€", "");
  if (!raw) return 0;
  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;
  const parsed = Number(normalized.replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyInput = (value) => {
  const parsed = typeof value === "string" ? parseCurrencyInput(value) : Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2).replace(".", ",") : "";
};

const formatCurrencyDisplay = (value) => `${parseCurrencyInput(value).toFixed(2).replace(".", ",")} €`;

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const sanitizeFilename = (value) =>
  String(value || "abrechnung")
    .trim()
    .replace(/[^a-z0-9äöüß._-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();

const SuffixInput = ({ suffix, className = "", onKeyDown = handleTableInputKeyDown, ...props }) => (
  <span className={`input-with-suffix ${className}`.trim()}>
    <input {...props} onKeyDown={onKeyDown} />
    <span>{suffix}</span>
  </span>
);

const hasPriceRowContent = (row) =>
  Boolean(
    String(row.getraenkeart || "").trim() ||
    String(row.markenfarbe || "").trim() ||
    String(row.festpreis || "").trim() ||
    String(row.bedienpreis || "").trim()
  );

const hasPfandRowContent = (row) =>
  Boolean(
    String(row.artikel || "").trim() ||
    String(row.farbe || "").trim() ||
    String(row.betrag || "").trim()
  );

const hasMarkenRowContent = (row) =>
  Boolean(
    String(row.name || "").trim() ||
    String(row.wert || "").trim()
  );

const hasCustomBillingContent = (row) =>
  Boolean(String(row.label || "").trim() || String(row.betrag || "").trim());

const hasDeliveryBillingContent = (row) =>
  Boolean(
    String(row.label || "").trim()
    || String(row.anzahl || "").trim()
    || String(row.preis || "").trim()
  );

const withTrailingEmptyPriceRow = (rows) => {
  const cleaned = rows.filter((row, index) => hasPriceRowContent(row) || index === rows.length - 1);
  return cleaned.length && !hasPriceRowContent(cleaned[cleaned.length - 1])
    ? cleaned
    : [...cleaned, createEmptyPriceRow()];
};

const withTrailingEmptyPfandRow = (rows) => {
  const cleaned = rows.filter((row, index) => hasPfandRowContent(row) || index === rows.length - 1);
  return cleaned.length && !hasPfandRowContent(cleaned[cleaned.length - 1])
    ? cleaned
    : [...cleaned, createEmptyPfandRow()];
};

const withTrailingEmptyMarkenRow = (rows) => {
  const cleaned = rows.filter((row, index) => hasMarkenRowContent(row) || index === rows.length - 1);
  return cleaned.length && !hasMarkenRowContent(cleaned[cleaned.length - 1])
    ? cleaned
    : [...cleaned, createEmptyMarkenRow()];
};

const formatDate = (value) => formatDateWithWeekday(value);

const parseWholeNumber = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const numeric = Number(raw.replace(",", "."));
  if (Number.isFinite(numeric)) return Math.trunc(numeric);
  const digits = Number.parseInt(raw.replace(/[^\d-]/g, ""), 10);
  return Number.isFinite(digits) ? digits : 0;
};

const formatQuantityInput = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(parseWholeNumber(value));
};

const sanitizeWholeInput = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d-]/g, "");
  return cleaned.startsWith("-")
    ? `-${cleaned.slice(1).replace(/-/g, "")}`
    : cleaned.replace(/-/g, "");
};

const calculateBillingAmount = (position) => {
  const anzahl = parseWholeNumber(position.anzahl);
  const zurueck = parseWholeNumber(position.zurueck);
  const preis = parseCurrencyInput(position.preis);
  if (position.position_type === "voucher") return -(anzahl * preis);
  if (position.position_type === "custom" && position.custom_type === "delivery_note") return 0;
  if (position.position_type === "custom") return -Math.abs(parseCurrencyInput(position.betrag));
  return (anzahl - zurueck) * preis;
};

const calculateDeliveryLineAmount = (position) =>
  parseWholeNumber(position.anzahl) * parseCurrencyInput(position.preis);

const deliveryNoteImageTypes = new Set([
  "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif", "image/avif",
  "image/heic", "image/heic-sequence", "image/heif", "image/heif-sequence",
  "image/bmp", "image/tiff",
]);

const calculateSoldCount = (position) =>
  Math.max(0, parseWholeNumber(position.anzahl) - parseWholeNumber(position.zurueck));

const formatRatio = (value) => Number.isFinite(value) ? value.toFixed(4).replace(".", ",") : "0,0000";

const formatPercent = (value) => Number.isFinite(value) ? `${value.toFixed(2).replace(".", ",")} %` : "0,00 %";

const EventDetail = ({ event, onBack }) => {
  const { showNotification } = useNotifications();
  const token = localStorage.getItem("token");
  let accountType = localStorage.getItem("accountType");
  if (!accountType && token) {
    try {
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      accountType = payload.accountType;
    } catch (err) {
      console.warn("Could not parse accountType from token", err);
    }
  }
  const [days, setDays] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tab, setTab] = useState("tage");
  const [availableWaiters, setAvailableWaiters] = useState([]);
  const [companyWaiters, setCompanyWaiters] = useState([]);

  // Teams state
  const [teams, setTeams] = useState([]);
  const [teamNameInput, setTeamNameInput] = useState("");
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualFirstName, setManualFirstName] = useState("");
  const [manualLastName, setManualLastName] = useState("");

  // Edit-Modus Flags
  const [priceEditMode, setPriceEditMode] = useState(false);
  const [pfandEditMode, setPfandEditMode] = useState(false);
  const [markenEditMode, setMarkenEditMode] = useState(false);
  const [dragPriceIndex, setDragPriceIndex] = useState(null);
  const [dragPfandIndex, setDragPfandIndex] = useState(null);
  const [dragMarkenIndex, setDragMarkenIndex] = useState(null);

  // State für Preis- und Pfand-Listen
  const [priceRows, setPriceRows] = useState([]);
  const [pfandRows, setPfandRows] = useState([]);
  const [markenRows, setMarkenRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayOverview, setDayOverview] = useState(null);
  const [selectedBillingUnit, setSelectedBillingUnit] = useState(null);
  const [billingDetail, setBillingDetail] = useState(null);
  const [activeDeliveryNoteKey, setActiveDeliveryNoteKey] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [collapsedBillingSections, setCollapsedBillingSections] = useState({});
  const [billingSettingsOpen, setBillingSettingsOpen] = useState(false);
  const [shiftNameDraft, setShiftNameDraft] = useState("");
  const [shiftModeDraft, setShiftModeDraft] = useState("included");
  const [shiftMemberDraft, setShiftMemberDraft] = useState([]);
  const [billingAutosaveStatus, setBillingAutosaveStatus] = useState("idle");
  const [billingAutosaveAt, setBillingAutosaveAt] = useState(null);
  const billingDetailRef = useRef(null);
  const selectedBillingUnitRef = useRef(null);
  const selectedDayRef = useRef(null);
  const billingDirtyRef = useRef(false);
  const billingSavePromiseRef = useRef(null);
  const billingAutosaveTimerRef = useRef(null);
  const billingAutosaveMaxTimerRef = useRef(null);
  const billingAutosaveVersionRef = useRef(0);
  const saveBillingNowRef = useRef(null);
  const viewerAccountType = billingDetail?.viewerAccountType || dayOverview?.viewerAccountType || accountType;


  const fetchJson = useCallback(async (path, options = {}) => {
    return apiFetch(path, options);
  }, []);

  const clearBillingAutosaveTimers = () => {
    if (billingAutosaveTimerRef.current) {
      window.clearTimeout(billingAutosaveTimerRef.current);
      billingAutosaveTimerRef.current = null;
    }
    if (billingAutosaveMaxTimerRef.current) {
      window.clearTimeout(billingAutosaveMaxTimerRef.current);
      billingAutosaveMaxTimerRef.current = null;
    }
  };

  useEffect(() => {
    billingDetailRef.current = billingDetail;
  }, [billingDetail]);

  useEffect(() => {
    selectedBillingUnitRef.current = selectedBillingUnit;
  }, [selectedBillingUnit]);

  useEffect(() => {
    selectedDayRef.current = selectedDay;
  }, [selectedDay]);

  useEffect(() => () => clearBillingAutosaveTimers(), []);

  const loadEventPriceData = useCallback(async () => {
    const [prices, pfand, marken] = await Promise.all([
      fetchJson(`/api/preislisten/${event.id}/preisliste`),
      fetchJson(`/api/preislisten/${event.id}/pfand`),
      fetchJson(`/api/preislisten/${event.id}/marken`),
    ]);
    setPriceRows(prices);
    setPfandRows(pfand);
    setMarkenRows(marken);
  }, [event.id, fetchJson]);

  const loadTemplates = useCallback(async () => {
    if (accountType !== "unternehmen") return;
    const rows = await fetchJson("/api/preislisten/vorlagen");
    setTemplates(rows);
  }, [accountType, fetchJson]);

  const loadStaffData = useCallback(async () => {
    const requests = [
      fetchJson(`/api/events/${event.id}/assignments`),
      fetchJson(`/api/events/${event.id}/teams`),
    ];

    if (accountType === "unternehmen") {
      requests.push(fetchJson(`/api/events/${event.id}/available-bedienungen`));
      requests.push(fetchJson(`/api/events/${event.id}/company-bedienungen`));
    }

    const [assignmentRows, teamRows, availableRows = [], companyRows = []] = await Promise.all(requests);
    setAssignments(assignmentRows);
    setTeams(teamRows);
    setAvailableWaiters(availableRows);
    setCompanyWaiters(companyRows);
  }, [accountType, event.id, fetchJson]);

  const enterPriceEditMode = () => {
    setPriceRows(prev => withTrailingEmptyPriceRow(prev.map(row => ({
      ...row,
      _draftId: row._draftId || createDraftId(),
      festpreis: formatCurrencyInput(row.festpreis),
      bedienpreis: formatCurrencyInput(row.bedienpreis),
    }))));
    setPriceEditMode(true);
  };

  const enterPfandEditMode = () => {
    setPfandRows(prev => withTrailingEmptyPfandRow(prev.map(row => ({
      ...row,
      _draftId: row._draftId || createDraftId(),
      betrag: formatCurrencyInput(row.betrag),
    }))));
    setPfandEditMode(true);
  };

  const enterMarkenEditMode = () => {
    setMarkenRows(prev => withTrailingEmptyMarkenRow(prev.map(row => ({
      ...row,
      _draftId: row._draftId || createDraftId(),
      wert: formatCurrencyInput(row.wert),
    }))));
    setMarkenEditMode(true);
  };

  // Aktualisiert eine bestehende Preislisten-Zeile im Edit-Modus
  const handlePriceRowChange = (index, field, value) => {
    setPriceRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === "getraenkeart" && String(value).trim() && !String(updated[index].markenfarbe || "").trim()
          ? { markenfarbe: "#be9e44" }
          : {}),
      };
      return withTrailingEmptyPriceRow(updated);
    });
  };

  const handlePriceAmountBlur = (index, field) => {
    setPriceRows(prev => {
      const updated = [...prev];
      if (hasPriceRowContent(updated[index])) {
        updated[index] = {
          ...updated[index],
          [field]: formatCurrencyInput(updated[index][field]),
        };
      }
      return withTrailingEmptyPriceRow(updated);
    });
  };

  const handleDeletePriceRow = (index) => {
    setPriceRows(prev => withTrailingEmptyPriceRow(prev.filter((_, idx) => idx !== index)));
  };

  const handleDropPriceRow = (dropIndex) => {
    setPriceRows(prev => {
      if (dragPriceIndex === null || dragPriceIndex === dropIndex) return prev;
      const movable = prev.filter(hasPriceRowContent);
      const [moved] = movable.splice(dragPriceIndex, 1);
      movable.splice(dropIndex, 0, moved);
      return withTrailingEmptyPriceRow(movable);
    });
    setDragPriceIndex(null);
  };

  // Aktualisiert eine bestehende Pfand-Zeile im Edit-Modus
  const handlePfandRowChange = (index, field, value) => {
    setPfandRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        ...(field === "artikel" && String(value).trim() && !String(updated[index].farbe || "").trim()
          ? { farbe: "#be9e44" }
          : {}),
      };
      return withTrailingEmptyPfandRow(updated);
    });
  };

  const handlePfandAmountBlur = (index) => {
    setPfandRows(prev => {
      const updated = [...prev];
      if (hasPfandRowContent(updated[index])) {
        updated[index] = {
          ...updated[index],
          betrag: formatCurrencyInput(updated[index].betrag),
        };
      }
      return withTrailingEmptyPfandRow(updated);
    });
  };

  const handleDeletePfandRow = (index) => {
    setPfandRows(prev => withTrailingEmptyPfandRow(prev.filter((_, idx) => idx !== index)));
  };

  const handleDropPfandRow = (dropIndex) => {
    setPfandRows(prev => {
      if (dragPfandIndex === null || dragPfandIndex === dropIndex) return prev;
      const movable = prev.filter(hasPfandRowContent);
      const [moved] = movable.splice(dragPfandIndex, 1);
      movable.splice(dropIndex, 0, moved);
      return withTrailingEmptyPfandRow(movable);
    });
    setDragPfandIndex(null);
  };

  const handleMarkenRowChange = (index, field, value) => {
    setMarkenRows(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return withTrailingEmptyMarkenRow(updated);
    });
  };

  const handleMarkenAmountBlur = (index) => {
    setMarkenRows(prev => {
      const updated = [...prev];
      if (hasMarkenRowContent(updated[index])) {
        updated[index] = {
          ...updated[index],
          wert: formatCurrencyInput(updated[index].wert),
        };
      }
      return withTrailingEmptyMarkenRow(updated);
    });
  };

  const handleDeleteMarkenRow = (index) => {
    setMarkenRows(prev => withTrailingEmptyMarkenRow(prev.filter((_, idx) => idx !== index)));
  };

  const handleDropMarkenRow = (dropIndex) => {
    setMarkenRows(prev => {
      if (dragMarkenIndex === null || dragMarkenIndex === dropIndex) return prev;
      const movable = prev.filter(hasMarkenRowContent);
      const [moved] = movable.splice(dragMarkenIndex, 1);
      movable.splice(dropIndex, 0, moved);
      return withTrailingEmptyMarkenRow(movable);
    });
    setDragMarkenIndex(null);
  };

  // Speichert die komplette Preisliste beim Backend und lädt sie neu
  const handleSavePriceList = async () => {
    try {
      // Filtere leere Zeilen heraus
      const entriesToSave = priceRows
        .filter(r => String(r.getraenkeart || "").trim() && String(r.markenfarbe || "").trim())
        .map(r => ({
          id: r.id,
          getraenkeart: String(r.getraenkeart).trim(),
          markenfarbe: String(r.markenfarbe).trim(),
          festpreis: parseCurrencyInput(r.festpreis),
          bedienpreis: parseCurrencyInput(r.bedienpreis),
        }));
      await fetchJson(`/api/preislisten/${event.id}/preisliste`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ entries: entriesToSave }),
      });
      await loadEventPriceData();
      setPriceEditMode(false);
    } catch (err) {
      console.error("Fehler beim Speichern der Preisliste", err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  // Speichert die komplette Pfandliste beim Backend und lädt sie neu
  const handleSavePfandList = async () => {
    try {
      // Filtere leere Zeilen heraus
      const itemsToSave = pfandRows
        .filter(r => String(r.artikel || "").trim() && String(r.farbe || "").trim())
        .map(r => ({
          id: r.id,
          artikel: String(r.artikel).trim(),
          farbe: String(r.farbe).trim(),
          betrag: parseCurrencyInput(r.betrag),
        }));
      await fetchJson(`/api/preislisten/${event.id}/pfand`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: itemsToSave }),
      });
      await loadEventPriceData();
      setPfandEditMode(false);
    } catch (err) {
      console.error("Fehler beim Speichern der Pfandliste", err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleSaveMarkenList = async () => {
    try {
      const itemsToSave = markenRows
        .filter(r => String(r.name || "").trim())
        .map(r => ({
          id: r.id,
          name: String(r.name).trim(),
          wert: parseCurrencyInput(r.wert),
        }));
      await fetchJson(`/api/preislisten/${event.id}/marken`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: itemsToSave }),
      });
      await loadEventPriceData();
      setMarkenEditMode(false);
      showNotification("success", "Markenliste wurde gespeichert.");
    } catch (err) {
      console.error("Fehler beim Speichern der Markenliste", err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleExportTemplate = async () => {
    const name = window.prompt("Name der neuen Vorlage:");
    if (!name?.trim()) return;

    try {
      const created = await fetchJson(`/api/preislisten/vorlagen/from-event/${event.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      });
      await loadTemplates();
      showNotification("success", `Vorlage "${created.name}" wurde gespeichert.`);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleApplyTemplate = async (templateId, mode) => {
    try {
      await fetchJson(`/api/preislisten/vorlagen/${templateId}/apply/${event.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode }),
      });
      await loadEventPriceData();
      setShowTemplatePicker(false);
      showNotification("success", "Vorlage wurde eingefügt.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const withTrailingEmptyBillingRows = (rows) => {
    const cleaned = rows.filter((row, index) => hasCustomBillingContent(row) || index === rows.length - 1);
    return cleaned.length && !hasCustomBillingContent(cleaned[cleaned.length - 1])
      ? cleaned
      : [...cleaned, createEmptyCustomBillingRow()];
  };

  const withTrailingEmptyDeliveryBillingRows = (rows, deliveryNoteKey) => {
    const cleaned = rows.filter((row, index) => hasDeliveryBillingContent(row) || index === rows.length - 1);
    return cleaned.length && !hasDeliveryBillingContent(cleaned[cleaned.length - 1])
      ? cleaned
      : [...cleaned, createEmptyDeliveryBillingRow(deliveryNoteKey)];
  };

  const prepareBillingPositions = (detail) => {
    const allPositions = detail?.positions || [];
    const activeDeliveryNoteKeys = new Set(
      allPositions
        .filter(pos => (
          pos.position_type === "custom"
          && pos.custom_type !== "delivery_note"
          && hasCustomBillingContent(pos)
          && pos.delivery_note_key
        ))
        .map(pos => pos.delivery_note_key)
    );
    return allPositions
      .filter(pos => {
        if (pos.position_type !== "custom") return true;
        if (pos.custom_type === "delivery_note") {
          return activeDeliveryNoteKeys.has(pos.delivery_note_key) && hasDeliveryBillingContent(pos);
        }
        return hasCustomBillingContent(pos);
      })
      .map((pos, index) => ({
        ...pos,
        custom_type: pos.position_type === "custom"
          ? (pos.custom_type === "delivery_note" ? "delivery_note" : "manual")
          : undefined,
        anzahl: pos.position_type === "custom" && pos.custom_type !== "delivery_note"
          ? null
          : parseWholeNumber(pos.anzahl),
        zurueck: pos.position_type === "voucher" || pos.position_type === "custom" ? null : parseWholeNumber(pos.zurueck),
        preis: parseCurrencyInput(pos.preis),
        betrag: pos.position_type === "custom" && pos.custom_type !== "delivery_note"
          ? Math.abs(parseCurrencyInput(pos.betrag))
          : calculateBillingAmount(pos),
        sort_order: index,
      }));
  };

  const saveBillingNow = async ({ silent = true, force = false, reload = false } = {}) => {
    if (billingSavePromiseRef.current) {
      const previousSaveSucceeded = await billingSavePromiseRef.current;
      if (!previousSaveSucceeded) return false;
    }

    const detail = billingDetailRef.current;
    const unit = selectedBillingUnitRef.current;
    const day = selectedDayRef.current;
    if (!detail || !unit || !day || !detail.canEdit) return true;
    if (!force && !billingDirtyRef.current) return true;

    const versionAtStart = billingAutosaveVersionRef.current;
    setBillingAutosaveStatus("saving");

    const savePromise = (async () => {
      try {
        await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${day.id}/units/${unit.unit_key}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shiftKey: detail.shift?.shift_key || "main",
            memberIds: shiftMemberDraft,
            useCurrentPriceSnapshots: Boolean(detail.useCurrentPriceSnapshots),
            positions: prepareBillingPositions(detail),
          }),
        });

        if (billingAutosaveVersionRef.current === versionAtStart) {
          billingDirtyRef.current = false;
          clearBillingAutosaveTimers();
          setBillingAutosaveStatus("saved");
          setBillingAutosaveAt(new Date());
        } else {
          setBillingAutosaveStatus("dirty");
          scheduleBillingAutosave();
        }

        const overview = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${day.id}/overview`);
        setDayOverview(overview);
        if (reload) {
          await loadBillingUnit(unit, false, day, detail.shift?.shift_key || "main", { skipAutosave: true });
        }
        if (!silent) showNotification("success", "Tagesabrechnung wurde gespeichert.");
        return true;
      } catch (err) {
        console.error(err);
        setBillingAutosaveStatus("error");
        if (!err.sessionExpired) showNotification("error", err.message);
        return false;
      }
    })();

    billingSavePromiseRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      if (billingSavePromiseRef.current === savePromise) billingSavePromiseRef.current = null;
    }
  };
  saveBillingNowRef.current = saveBillingNow;

  const scheduleBillingAutosave = () => {
    if (!billingDetailRef.current?.canEdit) return;
    if (billingAutosaveTimerRef.current) window.clearTimeout(billingAutosaveTimerRef.current);
    billingAutosaveTimerRef.current = window.setTimeout(() => {
      billingAutosaveTimerRef.current = null;
      saveBillingNow({ silent: true });
    }, 2000);

    if (!billingAutosaveMaxTimerRef.current) {
      billingAutosaveMaxTimerRef.current = window.setTimeout(() => {
        billingAutosaveMaxTimerRef.current = null;
        saveBillingNow({ silent: true });
      }, 60000);
    }
  };

  const markBillingDirty = () => {
    if (!billingDetailRef.current?.canEdit) return;
    billingDirtyRef.current = true;
    billingAutosaveVersionRef.current += 1;
    setBillingAutosaveStatus("dirty");
    scheduleBillingAutosave();
  };

  const flushBillingAutosave = async () => {
    if (!billingDirtyRef.current && !billingSavePromiseRef.current) return true;
    return saveBillingNow({ silent: true });
  };

  const loadDayOverview = async (day) => {
    try {
      if (!(await flushBillingAutosave())) return;
      setBillingLoading(true);
      setSelectedDay(day);
      setSelectedBillingUnit(null);
      setBillingDetail(null);
      const data = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${day.id}/overview`);
      setDayOverview(data);
      if ((data.viewerAccountType || accountType) === "bedienung" && data.units?.length === 1) {
        await loadBillingUnit(data.units[0], false, day);
      }
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    } finally {
      setBillingLoading(false);
    }
  };

  const loadBillingUnit = async (unit, refresh = false, dayOverride = selectedDay, shiftKeyOverride = null, options = {}) => {
    if (!dayOverride) return;
    try {
      if (!options.skipAutosave && !(await flushBillingAutosave())) return;
      setBillingLoading(true);
      setSelectedBillingUnit(unit);
      setBillingSettingsOpen(false);
      const query = new URLSearchParams();
      if (refresh) query.set("refresh", "1");
      if (shiftKeyOverride) query.set("shiftKey", shiftKeyOverride);
      const queryString = query.toString() ? `?${query.toString()}` : "";
      const data = await fetchJson(
        `/api/abrechnungen/events/${event.id}/tage/${dayOverride.id}/units/${unit.unit_key}${queryString}`
      );
      if (!refresh && data.hasSnapshotChanges) {
        const adoptCurrent = window.confirm(
          "Preisliste, Pfand oder Markenliste wurden seit dem Speichern geändert.\n\nAktuelle Liste übernehmen und Mengen behalten?"
        );
        if (adoptCurrent) {
          await loadBillingUnit(unit, true, dayOverride, data.shift?.shift_key || shiftKeyOverride);
          return;
        }
      }
      const positions = data.positions || [];
      const customRows = positions
        .filter(pos => pos.position_type === "custom" && pos.custom_type !== "delivery_note")
        .map(pos => ({
          ...pos,
          custom_type: "manual",
          betrag: formatCurrencyInput(Math.abs(parseCurrencyInput(pos.betrag))),
        }));
      const storedDeliveryRows = positions
        .filter(pos => pos.position_type === "custom" && pos.custom_type === "delivery_note")
        .map(pos => ({
          ...pos,
          custom_type: "delivery_note",
          anzahl: formatQuantityInput(pos.anzahl),
          zurueck: null,
          preis: formatCurrencyInput(pos.preis),
          betrag: 0,
        }));
      const deliveryNoteKeys = [...new Set(
        customRows.map(row => row.delivery_note_key).filter(Boolean)
      )];
      const deliveryRows = deliveryNoteKeys.flatMap(noteKey => {
        const relatedRows = storedDeliveryRows.filter(row => row.delivery_note_key === noteKey);
        return data.canEdit
          ? withTrailingEmptyDeliveryBillingRows(relatedRows, noteKey)
          : relatedRows.filter(hasDeliveryBillingContent);
      });
      setBillingDetail({
        ...data,
        deliveryNotes: data.deliveryNotes || [],
        useCurrentPriceSnapshots: Boolean(refresh),
        bedienungWallet: data.bedienungWallet
          ? {
            wallet_amount: formatCurrencyInput(data.bedienungWallet.wallet_amount),
            change_amount: formatCurrencyInput(data.bedienungWallet.change_amount),
          }
          : { wallet_amount: "", change_amount: "" },
        positions: [
          ...positions.filter(pos => pos.position_type !== "custom").map(pos => ({
            ...pos,
            anzahl: formatQuantityInput(pos.anzahl),
            zurueck: pos.position_type === "voucher" ? null : formatQuantityInput(pos.zurueck),
          })),
          ...withTrailingEmptyBillingRows(customRows),
          ...deliveryRows,
        ],
      });
      setActiveDeliveryNoteKey(null);
      billingDirtyRef.current = false;
      clearBillingAutosaveTimers();
      setBillingAutosaveStatus("idle");
      setShiftNameDraft(data.shift?.shift_name || "");
      setShiftModeDraft(data.shift?.shift_mode || "included");
      setShiftMemberDraft(data.shiftMembers || []);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    } finally {
      setBillingLoading(false);
    }
  };

  const updateBillingPosition = (index, field, value) => {
    setBillingDetail(prev => {
      const positions = [...(prev?.positions || [])];
      positions[index] = {
        ...positions[index],
        [field]: value,
      };
      const fixed = positions.filter(pos => pos.position_type !== "custom");
      const custom = withTrailingEmptyBillingRows(
        positions.filter(pos => pos.position_type === "custom" && pos.custom_type !== "delivery_note")
      );
      const delivery = positions.filter(pos => pos.position_type === "custom" && pos.custom_type === "delivery_note");
      return { ...prev, positions: [...fixed, ...custom, ...delivery] };
    });
    markBillingDirty();
  };

  const openDeliveryNote = (positionIndex) => {
    const parent = billingDetail?.positions?.[positionIndex];
    if (!parent) return;
    if (!String(parent.label || "").trim()) {
      showNotification("error", "Bitte zuerst einen Text für den Sonstiges-Eintrag eingeben.");
      return;
    }

    const noteKey = parent.delivery_note_key || `delivery-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    if (!parent.delivery_note_key) {
      setBillingDetail(prev => {
        const positions = [...(prev?.positions || [])];
        positions[positionIndex] = {
          ...positions[positionIndex],
          delivery_note_key: noteKey,
          betrag: "0,00",
        };
        return { ...prev, positions: [...positions, createEmptyDeliveryBillingRow(noteKey)] };
      });
      markBillingDirty();
    }
    setActiveDeliveryNoteKey(noteKey);
  };

  const updateDeliveryNotePosition = (noteKey, positionIndex, field, value) => {
    setBillingDetail(prev => {
      const positions = [...(prev?.positions || [])];
      positions[positionIndex] = { ...positions[positionIndex], [field]: value };

      const currentRows = positions.filter(
        pos => pos.position_type === "custom" && pos.custom_type === "delivery_note" && pos.delivery_note_key === noteKey
      );
      const noteRows = withTrailingEmptyDeliveryBillingRows(currentRows, noteKey);
      const noteAmount = noteRows
        .filter(hasDeliveryBillingContent)
        .reduce((sum, row) => sum + calculateDeliveryLineAmount(row), 0);
      const fixed = positions.filter(pos => pos.position_type !== "custom");
      const parents = positions
        .filter(pos => pos.position_type === "custom" && pos.custom_type !== "delivery_note")
        .map(pos => pos.delivery_note_key === noteKey
          ? { ...pos, betrag: formatCurrencyInput(noteAmount) }
          : pos
        );
      const otherDeliveryRows = positions.filter(
        pos => pos.position_type === "custom" && pos.custom_type === "delivery_note" && pos.delivery_note_key !== noteKey
      );
      return { ...prev, positions: [...fixed, ...parents, ...otherDeliveryRows, ...noteRows] };
    });
    markBillingDirty();
  };

  const handleDeliveryNoteImage = async (inputEvent, noteKey) => {
    const file = inputEvent.target.files?.[0];
    inputEvent.target.value = "";
    if (!file || !selectedBillingUnit || !selectedDay || !billingDetail) return;
    if (!deliveryNoteImageTypes.has(file.type)) {
      showNotification("error", "Bitte ein gängiges Bildformat auswählen, z. B. JPG, PNG, HEIC oder WebP.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showNotification("error", "Das Bild darf maximal 20 MB groß sein.");
      return;
    }

    try {
      if (!(await saveBillingNow({ silent: true, force: true }))) return;
      const imageData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Das Bild konnte nicht gelesen werden."));
        reader.readAsDataURL(file);
      });
      const data = await fetchJson(
        `/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/delivery-note`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shiftKey: billingDetail.shift?.shift_key || "main",
            noteKey,
            fileName: file.name,
            imageData,
          }),
        }
      );
      setBillingDetail(prev => ({
        ...prev,
        deliveryNotes: [
          ...(prev?.deliveryNotes || []).filter(note => note.noteKey !== noteKey),
          data.deliveryNote,
        ],
      }));
      showNotification("success", "Lieferscheinbild wurde gespeichert.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const deleteDeliveryNoteImage = async (noteKey) => {
    if (!selectedBillingUnit || !selectedDay) return;
    try {
      if (!(await flushBillingAutosave())) return;
      await fetchJson(
        `/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/delivery-note`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shiftKey: billingDetail.shift?.shift_key || "main", noteKey }),
        }
      );
      setBillingDetail(prev => ({
        ...prev,
        deliveryNotes: (prev?.deliveryNotes || []).filter(note => note.noteKey !== noteKey),
      }));
      showNotification("success", "Lieferscheinbild wurde entfernt.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const saveBillingUnit = async () => {
    await saveBillingNow({ silent: false, force: true, reload: true });
  };

  const updateBedienungWallet = (field, value) => {
    setBillingDetail(prev => ({
      ...prev,
      bedienungWallet: {
        ...(prev?.bedienungWallet || {}),
        [field]: value,
      },
    }));
  };

  const saveBedienungWallet = async () => {
    if (!selectedBillingUnit || !selectedDay || !billingDetail) return;
    try {
      const wallet = billingDetail.bedienungWallet || {};
      const saved = await fetchJson(
        `/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/wallet`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shiftKey: billingDetail.shift?.shift_key || "main",
            wallet_amount: parseCurrencyInput(wallet.wallet_amount),
            change_amount: parseCurrencyInput(wallet.change_amount),
          }),
        }
      );
      setBillingDetail(prev => ({
        ...prev,
        bedienungWallet: {
          wallet_amount: formatCurrencyInput(saved.wallet_amount),
          change_amount: formatCurrencyInput(saved.change_amount),
        },
      }));
      showNotification("success", "Geldbeutelwerte wurden gespeichert.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  useEffect(() => {
    const flushOnLeave = () => {
      if (billingDirtyRef.current) saveBillingNowRef.current?.({ silent: true });
    };
    const flushOnHidden = () => {
      if (document.visibilityState === "hidden") flushOnLeave();
    };

    window.addEventListener("pagehide", flushOnLeave);
    window.addEventListener("beforeunload", flushOnLeave);
    document.addEventListener("visibilitychange", flushOnHidden);

    return () => {
      window.removeEventListener("pagehide", flushOnLeave);
      window.removeEventListener("beforeunload", flushOnLeave);
      document.removeEventListener("visibilitychange", flushOnHidden);
      flushOnLeave();
    };
  }, []);

  const createBillingShift = async () => {
    if (!selectedBillingUnit || !selectedDay) return;
    if (!(await flushBillingAutosave())) return;
    const name = window.prompt(
      viewerAccountType === "bedienung" ? "Name deiner privaten Schicht:" : "Name der neuen Schicht:",
      viewerAccountType === "bedienung" ? "Private Schicht" : "Neue Schicht"
    );
    if (!name?.trim()) return;

    try {
      const created = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/shifts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          mode: viewerAccountType === "bedienung" ? "ignored" : "included",
        }),
      });
      showNotification("success", "Schicht wurde erstellt.");
      await loadBillingUnit(selectedBillingUnit, false, selectedDay, created.shift_key);
      const overview = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/overview`);
      setDayOverview(overview);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const toggleShiftMemberDraft = (memberId) => {
    setShiftMemberDraft(prev => {
      const normalized = Number(memberId);
      return prev.map(Number).includes(normalized)
        ? prev.filter(id => Number(id) !== normalized)
        : [...prev, normalized];
    });
  };

  const saveBillingShiftSettings = async () => {
    if (!billingDetail?.shift || !selectedBillingUnit || !selectedDay) return;
    try {
      if (!(await flushBillingAutosave())) return;
      await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/shifts/${billingDetail.shift.shift_key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: shiftNameDraft,
          mode: viewerAccountType === "bedienung" ? "ignored" : shiftModeDraft,
          memberIds: shiftMemberDraft,
        }),
      });
      showNotification("success", "Schichteinstellungen wurden gespeichert.");
      await loadBillingUnit(selectedBillingUnit, false, selectedDay, billingDetail.shift.shift_key);
      const overview = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/overview`);
      setDayOverview(overview);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const deleteBillingShift = async () => {
    if (!billingDetail?.shift || !selectedBillingUnit || !selectedDay) return;
    const shiftName = billingDetail.shift.shift_name || "diese Schicht";
    const confirmed = window.confirm(`Soll ${shiftName} wirklich gelöscht werden?`);
    if (!confirmed) return;

    try {
      if (!(await flushBillingAutosave())) return;
      await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/units/${selectedBillingUnit.unit_key}/shifts/${billingDetail.shift.shift_key}`, {
        method: "DELETE",
      });
      showNotification("success", "Schicht wurde gelöscht.");
      const overview = await fetchJson(`/api/abrechnungen/events/${event.id}/tage/${selectedDay.id}/overview`);
      setDayOverview(overview);
      const nextUnit = overview.units?.find(unit => unit.unit_key === selectedBillingUnit.unit_key) || selectedBillingUnit;
      await loadBillingUnit(nextUnit, false, selectedDay);
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const buildPdfSection = (type, title, columns, rowFilter = () => true) => {
    const rows = (billingDetail?.positions || [])
      .filter(pos => pos.position_type === type)
      .filter(rowFilter)
      .filter(pos => {
        if (pos.position_type !== "custom") return true;
        return pos.custom_type === "delivery_note"
          ? hasDeliveryBillingContent(pos)
          : hasCustomBillingContent(pos);
      });
    if (!rows.length) return "";

    const bodyRows = rows.map(row => {
      const amount = calculateBillingAmount(row);
      return `<tr>${columns.map(column => {
        if (column.key === "label") {
          const colorStyle = ["drink", "pfand"].includes(type) ? getMarkColorStyle(row.farbe) : undefined;
          const inlineStyle = colorStyle
            ? ` style="background-color:${colorStyle.backgroundColor};color:${colorStyle.color}"`
            : "";
          return `<td${inlineStyle}>${escapeHtml(row.label || "-")}</td>`;
        }
        if (column.key === "anzahl") return `<td class="num">${parseWholeNumber(row.anzahl)} Stk.</td>`;
        if (column.key === "zurueck") return `<td class="num">${parseWholeNumber(row.zurueck)} Stk.</td>`;
        if (column.key === "preis") return `<td class="num">${formatCurrencyDisplay(row.preis)}</td>`;
        if (column.key === "betrag") {
          return `<td class="num">${formatCurrencyDisplay(amount)}</td>`;
        }
        return "<td></td>";
      }).join("")}</tr>`;
    }).join("");

    return `
      <section>
        <h2>${escapeHtml(title)}</h2>
        <table>
          <thead>
            <tr>${columns.map(column => `<th>${escapeHtml(column.label)}</th>`).join("")}</tr>
          </thead>
          <tbody>${bodyRows}</tbody>
          <tfoot>
            <tr>
              <td colspan="${Math.max(columns.length - 1, 1)}">Summe ${escapeHtml(title)}</td>
              <td class="num">${formatCurrencyDisplay(rows.reduce((sum, row) => sum + calculateBillingAmount(row), 0))}</td>
            </tr>
          </tfoot>
        </table>
      </section>
    `;
  };

  const exportBillingPdf = () => {
    if (!billingDetail || !selectedBillingUnit || !selectedDay) return;

    const total = billingDetail.positions.reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);
    const sections = [
      buildPdfSection("drink", "Getränke", [
        { key: "label", label: "Art" },
        { key: "anzahl", label: "Anzahl" },
        { key: "zurueck", label: "Zurück" },
        { key: "preis", label: "Preis" },
        { key: "betrag", label: "Umsatz" },
      ]),
      buildPdfSection("voucher", "Marken", [
        { key: "label", label: "Marke" },
        { key: "anzahl", label: "Anzahl" },
        { key: "preis", label: "Wert" },
        { key: "betrag", label: "Umsatz" },
      ]),
      buildPdfSection("custom", "Sonstiges", [
        { key: "label", label: "Text" },
        { key: "betrag", label: "Umsatz" },
      ], pos => pos.custom_type !== "delivery_note"),
      buildPdfSection("pfand", "Pfand", [
        { key: "label", label: "Art" },
        { key: "anzahl", label: "Anzahl" },
        { key: "zurueck", label: "Zurück" },
        { key: "preis", label: "Preis" },
        { key: "betrag", label: "Umsatz" },
      ]),
    ].join("");
    const summaryRows = [
      ["Getränke", getBillingSectionSum("drink")],
      ["Marken", getBillingSectionSum("voucher")],
      ["Sonstiges", getBillingSectionSum("custom")],
      ["Pfand", getBillingSectionSum("pfand")],
    ];
    const unitType = selectedBillingUnit.unit_type === "team" ? "Team" : "Bedienung";
    const shiftName = billingDetail.shift?.shift_name || "Hauptabrechnung";
    const title = `Abrechnung ${event.name} ${formatDate(selectedDay.datum)} ${selectedBillingUnit.name} ${shiftName}`;
    const filename = sanitizeFilename(`${event.name}-${formatDate(selectedDay.datum)}-${selectedBillingUnit.name}-${shiftName}`);
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      showNotification("error", "PDF-Fenster konnte nicht geöffnet werden.");
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html lang="de">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(filename)}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              color: #000;
              background: #fff;
              font-family: Arial, Helvetica, sans-serif;
              font-size: 11px;
              line-height: 1.3;
            }
            h1, h2, p { margin: 0; }
            h1 { font-size: 20px; margin-bottom: 8px; }
            h2 {
              font-size: 13px;
              margin: 14px 0 5px;
              padding-bottom: 3px;
              border-bottom: 1px solid #000;
            }
            .meta {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 4px 18px;
              margin-bottom: 12px;
              padding: 8px 0;
              border-top: 2px solid #000;
              border-bottom: 2px solid #000;
            }
            .meta strong { display: inline-block; min-width: 92px; }
            table {
              width: 100%;
              border-collapse: collapse;
              page-break-inside: avoid;
            }
            th, td {
              border: 1px solid #000;
              padding: 4px 5px;
              vertical-align: top;
            }
            th {
              background: #f0f0f0;
              font-weight: 700;
              text-align: left;
            }
            tfoot td {
              font-weight: 700;
              background: #f7f7f7;
            }
            .num { text-align: right; white-space: nowrap; }
            .summary {
              margin-top: 16px;
              page-break-inside: avoid;
            }
            .summary table { font-size: 12px; }
            .total td {
              border-top: 2px solid #000;
              font-size: 14px;
              font-weight: 700;
              background: #e8e8e8;
            }
            .signatures {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 28px;
              margin-top: 30px;
              page-break-inside: avoid;
            }
            .signature {
              padding-top: 28px;
              border-top: 1px solid #000;
              text-align: center;
              font-size: 11px;
            }
            .print-actions {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
              margin: 0 0 12px;
            }
            .print-actions button {
              border: 1px solid #000;
              background: #fff;
              color: #000;
              padding: 6px 10px;
              cursor: pointer;
              font-weight: 700;
            }
            @media print {
              .print-actions { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="print-actions">
            <button onclick="window.print()">PDF speichern / Drucken</button>
          </div>
          <h1>${escapeHtml(title)}</h1>
          <div class="meta">
            <p><strong>Fest:</strong> ${escapeHtml(event.name)}</p>
            <p><strong>Datum:</strong> ${escapeHtml(formatDate(selectedDay.datum))}</p>
            <p><strong>${escapeHtml(unitType)}:</strong> ${escapeHtml(selectedBillingUnit.name)}</p>
            <p><strong>Schicht:</strong> ${escapeHtml(shiftName)}</p>
            <p><strong>Erstellt am:</strong> ${escapeHtml(formatDate(new Date()))}</p>
          </div>
          ${sections}
          <section class="summary">
            <h2>Summen</h2>
            <table>
              <tbody>
                ${summaryRows.map(([label, value]) => `
                  <tr>
                    <td>${escapeHtml(label)}</td>
                    <td class="num">${formatCurrencyDisplay(value)}</td>
                  </tr>
                `).join("")}
                <tr class="total">
                  <td>Abzugeben</td>
                  <td class="num">${formatCurrencyDisplay(total)}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <div class="signatures">
            <div class="signature">Unterschrift Bedienung</div>
            <div class="signature">Unterschrift Unternehmen</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 250);
  };

  useEffect(() => {
    setSelectedDay(null);
    setDayOverview(null);
    setSelectedBillingUnit(null);
    setBillingDetail(null);
    Promise.all([
      fetchJson(`/api/events/${event.id}/tage`),
      fetchJson(`/api/events/${event.id}/assignments`),
    ])
      .then(([daysData, assignmentsData]) => {
        setDays(daysData);
        setAssignments(assignmentsData);
      })
      .catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      });
  }, [event.id, fetchJson, showNotification]);

  // Lade Preis- und Pfand-Daten beim Wechsel in den Preislisten-Tab
  useEffect(() => {
    if (tab === "preislisten") {
      loadEventPriceData().catch(console.error);
      loadTemplates().catch(console.error);
    }
  }, [tab, loadEventPriceData, loadTemplates]);

  // Lade Teams für dieses Event, wenn Tab "bedienungen"
  useEffect(() => {
    if (tab !== "bedienungen") return;
    loadStaffData()
      .catch(err => {
        console.error(err);
        if (!err.sessionExpired) showNotification("error", err.message);
      });
  }, [tab, loadStaffData, showNotification]);

  const handleAssignWaiter = async (waiter) => {
    try {
      await fetchJson(`/api/events/${event.id}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: waiter.type,
          userId: waiter.user_id,
          manualId: waiter.manual_id,
        }),
      });
      await loadStaffData();
      showNotification("success", "Bedienung wurde zum Event hinzugefügt.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    try {
      await fetchJson(`/api/events/${event.id}/assignments/${assignment.assignment_id}`, { method: "DELETE" });
      await loadStaffData();
      showNotification(
        "success",
        assignment.source === "manual"
          ? "Manuelle Bedienung wurde entfernt."
          : "Bedienung wurde aus dem Event entfernt."
      );
    } catch (err) {
      console.error(err);
      if (err.requiresConfirmation) {
        const teamNote = assignment.source === "manual" && err.teamName
          ? `Die übrigen Teamdaten von "${err.teamName}" bleiben erhalten.`
          : "Die eigenen Tagesabrechnungen und Kassenbeträge dieser Bedienung werden gelöscht.";
        const confirmed = window.confirm(
          `${err.message}\n\n${teamNote}\n\nBedienung samt diesen Daten wirklich entfernen?`
        );
        if (!confirmed) return;

        try {
          await fetchJson(`/api/events/${event.id}/assignments/${assignment.assignment_id}?deleteData=1`, {
            method: "DELETE",
          });
          await loadStaffData();
          showNotification("success", "Bedienung und ihre eigenen Daten wurden entfernt.");
        } catch (confirmErr) {
          console.error(confirmErr);
          if (!confirmErr.sessionExpired) showNotification("error", confirmErr.message);
        }
        return;
      }
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleCreateManualWaiter = async () => {
    if (!manualFirstName.trim() || !manualLastName.trim()) return;
    try {
      await fetchJson(`/api/events/${event.id}/manual-bedienungen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: manualFirstName.trim(),
          lastName: manualLastName.trim(),
        }),
      });
      setManualFirstName("");
      setManualLastName("");
      setShowManualForm(false);
      await loadStaffData();
      showNotification("success", "Manuelle Bedienung wurde angelegt.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleEditManualWaiter = async (assignment) => {
    const firstName = window.prompt("Vorname:", assignment.first_name);
    if (firstName === null) return;
    const lastName = window.prompt("Nachname:", assignment.last_name);
    if (lastName === null) return;

    try {
      await fetchJson(`/api/events/${event.id}/manual-bedienungen/${assignment.manual_bedienung_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName }),
      });
      await loadStaffData();
      showNotification("success", "Manuelle Bedienung wurde aktualisiert.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleReplaceManualWaiter = async (assignmentId, userId) => {
    if (!userId) return;
    try {
      await fetchJson(`/api/events/${event.id}/assignments/${assignmentId}/replace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      await loadStaffData();
      showNotification("success", "Manuelle Bedienung wurde verknüpft.");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleDeleteAvailableManualWaiter = async (waiter) => {
    try {
      await fetchJson(`/api/events/${event.id}/manual-bedienungen/${waiter.manual_id}`, { method: "DELETE" });
      await loadStaffData();
      showNotification("success", "Manuelle Bedienung wurde gelöscht.");
    } catch (err) {
      console.error(err);
      if (err.requiresConfirmation) {
        const confirmed = window.confirm(
          `${err.message}\n\nNur die Daten dieser Bedienung löschen und die manuelle Bedienung entfernen?`
        );
        if (!confirmed) return;

        try {
          await fetchJson(`/api/events/${event.id}/manual-bedienungen/${waiter.manual_id}?deleteData=1`, {
            method: "DELETE",
          });
          await loadStaffData();
          showNotification("success", "Manuelle Bedienung und ihre eigenen Daten wurden gelöscht.");
        } catch (confirmErr) {
          console.error(confirmErr);
          if (!confirmErr.sessionExpired) showNotification("error", confirmErr.message);
        }
        return;
      }
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleTeamAssignmentChange = async (assignmentId, teamId) => {
    try {
      if (teamId) {
        await fetchJson(`/api/teams/${teamId}/members`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assignmentId }),
        });
      } else {
        await fetchJson(`/api/events/${event.id}/assignments/${assignmentId}/team`, { method: "DELETE" });
      }
      await loadStaffData();
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  // Create a new team
  const handleCreateTeam = async () => {
    if (!teamNameInput.trim()) return;
    try {
      await fetchJson(`/api/events/${event.id}/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: teamNameInput }),
      });
      await loadStaffData();
      setShowTeamForm(false);
      setTeamNameInput("");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  // Start editing a team's name
  const handleStartEditTeam = (team) => {
    setEditingTeamId(team.id);
    setEditingTeamName(team.name);
  };

  // Save edited team name
  const handleSaveTeam = async (teamId) => {
    try {
      await fetchJson(`/api/teams/${teamId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editingTeamName }),
      });
      await loadStaffData();
      setEditingTeamId(null);
      setEditingTeamName("");
    } catch (err) {
      console.error(err);
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  // Delete a team
  const handleDeleteTeam = async (teamId) => {
    try {
      await fetchJson(`/api/teams/${teamId}`, { method: "DELETE" });
      await loadStaffData();
    } catch (err) {
      console.error(err);
      if (err.requiresConfirmation) {
        const confirmed = window.confirm(
          `${err.message}\n\nAlle Tagesabrechnungen und Kassenbeträge dieses Teams werden gelöscht. Team wirklich entfernen?`
        );
        if (!confirmed) return;
        try {
          await fetchJson(`/api/teams/${teamId}?deleteData=1`, { method: "DELETE" });
          await loadStaffData();
          showNotification("success", "Team und zugehörige Abrechnungsdaten wurden gelöscht.");
        } catch (confirmErr) {
          console.error(confirmErr);
          if (!confirmErr.sessionExpired) showNotification("error", confirmErr.message);
        }
        return;
      }
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const getBillingSectionSum = (type, rowFilter = () => true) =>
    (billingDetail?.positions || [])
      .filter(pos => pos.position_type === type)
      .filter(rowFilter)
      .reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);

  const toggleBillingSection = (type) => {
    setCollapsedBillingSections(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const renderBedienungPayout = () => {
    if (viewerAccountType !== "bedienung" || !billingDetail) return null;

    const shiftMemberIds = (billingDetail.shiftMembers?.length
      ? billingDetail.shiftMembers
      : billingDetail.shift?.member_ids || []
    ).map(Number);
    const ownMemberIds = (billingDetail.currentUserMemberIds || []).map(Number);
    const ownMemberCount = shiftMemberIds.filter(id => ownMemberIds.includes(id)).length || 1;
    const memberCount = Math.max(shiftMemberIds.length || 1, 1);

    const shareRows = (billingDetail.positions || [])
      .filter(pos => pos.position_type === "drink")
      .map(pos => {
        const sold = calculateSoldCount(pos);
        const sharePrice = Math.max(0, Number(pos.anteil_preis ?? (Number(pos.festpreis || 0) - Number(pos.preis || 0))));
        return {
          key: `${pos.source_id || pos.label}-${pos.farbe}`,
          label: pos.label || pos.farbe || "-",
          color: pos.farbe,
          sold,
          sharePrice,
          drinkRevenue: sold * parseCurrencyInput(pos.preis),
          gain: sold * sharePrice,
        };
      })
      .filter(row => row.sold > 0 || row.sharePrice > 0);

    const revenueShare = shareRows.reduce((sum, row) => sum + row.gain, 0);
    const drinkRevenue = shareRows.reduce((sum, row) => sum + row.drinkRevenue, 0);
    const totalCustomerRevenue = revenueShare + drinkRevenue;
    const wallet = billingDetail.bedienungWallet || {};
    const walletAmount = parseCurrencyInput(wallet.wallet_amount);
    const changeAmount = parseCurrencyInput(wallet.change_amount);
    const walletWithoutChange = walletAmount - changeAmount;
    const tip = walletWithoutChange - revenueShare;
    const tipShare = walletWithoutChange > 0 ? tip / walletWithoutChange : 0;
    const revenueRatio = walletWithoutChange > 0 ? revenueShare / walletWithoutChange : 0;
    const tipPercent = totalCustomerRevenue > 0 ? (tip / totalCustomerRevenue) * 100 : 0;
    const payoutPerPerson = memberCount > 0 ? walletWithoutChange / memberCount : walletWithoutChange;

    return (
      <section className="bedienung-payout-panel">
        <div className="bedienung-payout-header">
          <h3>Dein Umsatzanteil</h3>
          <span>{ownMemberCount}/{memberCount} Personen</span>
        </div>
        <div className="bedienung-payout-grid">
          <div className="bedienung-share-table-wrap">
            <table className="bedienung-share-table">
              <thead>
                <tr>
                  <th>Markenart</th>
                  <th>Verkauft</th>
                  <th>Anteil</th>
                  <th>Gewinn</th>
                </tr>
              </thead>
              <tbody>
                {shareRows.length ? shareRows.map(row => (
                  <tr key={row.key}>
                    <td className="drink-kind-cell" data-label="Markenart" style={getMarkColorStyle(row.color)}>{row.label}</td>
                    <td data-label="Verkauft">{row.sold} Stk.</td>
                    <td data-label="Anteil">{formatCurrencyDisplay(row.sharePrice)}</td>
                    <td data-label="Gewinn">{formatCurrencyDisplay(row.gain)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4">Noch keine Getränkeumsätze vorhanden.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3">Umsatzanteil</td>
                  <td>{formatCurrencyDisplay(revenueShare)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="bedienung-wallet-card">
            <label>
              Geld im Geldbeutel
              <SuffixInput
                suffix="€"
                type="text"
                inputMode="decimal"
                value={wallet.wallet_amount ?? ""}
                onChange={e => updateBedienungWallet("wallet_amount", e.target.value)}
                onBlur={e => updateBedienungWallet("wallet_amount", formatCurrencyInput(e.target.value))}
              />
            </label>
            <label>
              Wechselgeld
              <SuffixInput
                suffix="€"
                type="text"
                inputMode="decimal"
                value={wallet.change_amount ?? ""}
                onChange={e => updateBedienungWallet("change_amount", e.target.value)}
                onBlur={e => updateBedienungWallet("change_amount", formatCurrencyInput(e.target.value))}
              />
            </label>
            <div className="bedienung-wallet-summary">
              <div><span>Ohne Wechselgeld</span><strong>{formatCurrencyDisplay(walletWithoutChange)}</strong></div>
              <div><span>Trinkgeld</span><strong>{formatCurrencyDisplay(tip)}</strong></div>
              <div><span>Umsatz-Verhältnis</span><strong>{formatRatio(revenueRatio)}</strong></div>
              <div><span>Trinkgeld-Verhältnis</span><strong>{formatRatio(tipShare)}</strong></div>
              <div><span>Gesamtumsatz</span><strong>{formatCurrencyDisplay(totalCustomerRevenue)}</strong></div>
              <div><span>Trinkgeld auf Umsatz</span><strong>{formatPercent(tipPercent)}</strong></div>
              <div><span>Pro Person</span><strong>{formatCurrencyDisplay(payoutPerPerson)}</strong></div>
            </div>
            <button type="button" onClick={saveBedienungWallet}>
              Geldwerte speichern
            </button>
          </div>
        </div>
      </section>
    );
  };

  const renderBillingRows = (type, title, rowFilter = () => true, collapseKey = type) => {
    const rows = billingDetail?.positions?.filter(pos => pos.position_type === type && rowFilter(pos)) || [];
    if (!rows.length) return null;
    const sectionSum = rows.reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);
    const isCollapsed = Boolean(collapsedBillingSections[collapseKey]);
    const readOnly = !billingDetail?.canEdit;
    const isColoredItem = type === "drink" || type === "pfand";
    const canOpenDeliveryNote = !billingDetail?.isAggregate && billingDetail?.shift?.shift_key !== "gesamt";

    return (
      <section className={`billing-section ${isCollapsed ? "collapsed" : ""}`}>
        <button
          type="button"
          className="billing-section-toggle"
          onClick={() => toggleBillingSection(collapseKey)}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
          <span>{title}</span>
        </button>
        {!isCollapsed && (
          <table className={`billing-table billing-table-${type}`}>
            <colgroup>
              {type === "custom" ? (
                <>
                  <col className="billing-col-label" />
                  <col className="billing-col-amount" />
                </>
              ) : (
                <>
                  <col className="billing-col-label" />
                  <col className="billing-col-count" />
                  {type !== "voucher" && <col className="billing-col-count" />}
                  <col className="billing-col-money" />
                  <col className="billing-col-money" />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th>{type === "voucher" ? "Marke" : type === "custom" ? "Text" : "Art"}</th>
                {type !== "custom" && <th>Anzahl</th>}
                {type !== "voucher" && type !== "custom" && <th>Zurück</th>}
                {type !== "custom" && <th>{type === "voucher" ? "Wert" : "Preis"}</th>}
                <th>Umsatz</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const positionIndex = billingDetail.positions.indexOf(row);
                const amount = calculateBillingAmount(row);
                const isCustomPlaceholder = type === "custom" && !hasCustomBillingContent(row);
                const hasDeliveryNote = type === "custom" && Boolean(row.delivery_note_key);
                return (
                  <tr key={row.id ?? row._draftId ?? `${row.position_type}-${positionIndex}`} className={isCustomPlaceholder ? "placeholder-row" : ""}>
                    <td
                      className={isColoredItem ? "billing-kind-cell" : undefined}
                      data-label={type === "voucher" ? "Marke" : type === "custom" ? "Text" : "Art"}
                      style={isColoredItem ? getMarkColorStyle(row.farbe) : undefined}
                    >
                      {type === "custom" ? (
                        <div className="custom-billing-entry">
                          {readOnly ? (
                            <span className="billing-label">{row.label || "-"}</span>
                          ) : (
                            <input
                              type="text"
                              placeholder="Sonstiges"
                              value={row.label || ""}
                              onKeyDown={handleTableInputKeyDown}
                              onChange={e => updateBillingPosition(positionIndex, "label", e.target.value)}
                            />
                          )}
                          {canOpenDeliveryNote && (!readOnly || hasDeliveryNote) && !isCustomPlaceholder && (
                            <button
                              type="button"
                              className="delivery-note-open-button"
                              onClick={() => openDeliveryNote(positionIndex)}
                              aria-label={`Lieferschein für ${row.label || "diesen Eintrag"} öffnen`}
                              title="Lieferschein öffnen"
                            >
                              <FaChevronRight />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="billing-label">
                          {row.label}
                        </span>
                      )}
                    </td>
                    {type !== "custom" && (
                      <td data-label="Anzahl">
                        <SuffixInput
                          suffix="Stk."
                          type="text"
                          inputMode="numeric"
                          value={row.anzahl ?? ""}
                          readOnly={readOnly}
                          onChange={e => updateBillingPosition(
                            positionIndex,
                            "anzahl",
                            sanitizeWholeInput(e.target.value)
                          )}
                        />
                      </td>
                    )}
                    {type !== "voucher" && type !== "custom" && (
                      <td data-label="Zurück">
                        <SuffixInput
                          suffix="Stk."
                          type="text"
                          inputMode="numeric"
                          value={row.zurueck ?? ""}
                          readOnly={readOnly}
                          onChange={e => updateBillingPosition(positionIndex, "zurueck", sanitizeWholeInput(e.target.value))}
                        />
                      </td>
                    )}
                    {type !== "custom" && (
                      <td data-label={type === "voucher" ? "Wert" : "Preis"}>
                        {formatCurrencyDisplay(row.preis)}
                      </td>
                    )}
                    <td data-label="Umsatz">
                      {type === "custom" ? (
                        <SuffixInput
                          suffix="€"
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={row.betrag ?? ""}
                          readOnly={readOnly || hasDeliveryNote}
                          onChange={e => updateBillingPosition(positionIndex, "betrag", e.target.value)}
                          onBlur={e => updateBillingPosition(positionIndex, "betrag", formatCurrencyInput(e.target.value))}
                        />
                      ) : (
                        formatCurrencyDisplay(amount)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="billing-section-total">
          <span>Summe {title}</span>
          <strong>{formatCurrencyDisplay(sectionSum)}</strong>
        </div>
      </section>
    );
  };

  const renderDeliveryNoteDialog = () => {
    if (!activeDeliveryNoteKey || !billingDetail) return null;
    const parent = billingDetail.positions.find(position => (
      position.position_type === "custom"
      && position.custom_type !== "delivery_note"
      && position.delivery_note_key === activeDeliveryNoteKey
    ));
    if (!parent) return null;

    const readOnly = !billingDetail.canEdit;
    const rows = billingDetail.positions.filter(position => (
      position.position_type === "custom"
      && position.custom_type === "delivery_note"
      && position.delivery_note_key === activeDeliveryNoteKey
    ));
    const visibleRows = readOnly ? rows.filter(hasDeliveryBillingContent) : rows;
    const total = visibleRows
      .filter(hasDeliveryBillingContent)
      .reduce((sum, row) => sum + calculateDeliveryLineAmount(row), 0);
    const attachment = (billingDetail.deliveryNotes || []).find(note => note.noteKey === activeDeliveryNoteKey);

    return (
      <div className="delivery-note-dialog-backdrop" role="presentation" onMouseDown={() => setActiveDeliveryNoteKey(null)}>
        <section
          className="delivery-note-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={`Lieferschein für ${parent.label}`}
          onMouseDown={event => event.stopPropagation()}
        >
          <div className="delivery-note-dialog-header">
            <div>
              <span>Lieferschein</span>
              <h3>{parent.label}</h3>
            </div>
            <button type="button" onClick={() => setActiveDeliveryNoteKey(null)} aria-label="Lieferschein schließen">
              <FaTimes />
            </button>
          </div>
          <div className="delivery-note-dialog-content">
            <table className="billing-table billing-table-delivery-note">
              <colgroup>
                <col className="billing-col-label" />
                <col className="billing-col-count" />
                <col className="billing-col-money" />
                <col className="billing-col-money" />
              </colgroup>
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Anzahl</th>
                  <th>Preis</th>
                  <th>Betrag</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(row => {
                  const positionIndex = billingDetail.positions.indexOf(row);
                  const isPlaceholder = !hasDeliveryBillingContent(row);
                  return (
                    <tr key={row.id ?? row._draftId ?? `delivery-${positionIndex}`} className={isPlaceholder ? "placeholder-row" : ""}>
                      <td data-label="Position">
                        {readOnly ? <span className="billing-label">{row.label || "-"}</span> : (
                          <input
                            type="text"
                            placeholder="Getränk / Position"
                            value={row.label || ""}
                            onKeyDown={handleTableInputKeyDown}
                            onChange={input => updateDeliveryNotePosition(activeDeliveryNoteKey, positionIndex, "label", input.target.value)}
                          />
                        )}
                      </td>
                      <td data-label="Anzahl">
                        {readOnly ? `${parseWholeNumber(row.anzahl)} Stk.` : (
                          <SuffixInput
                            suffix="Stk."
                            type="text"
                            inputMode="numeric"
                            value={row.anzahl ?? ""}
                            onChange={input => updateDeliveryNotePosition(
                              activeDeliveryNoteKey,
                              positionIndex,
                              "anzahl",
                              sanitizeWholeInput(input.target.value)
                            )}
                          />
                        )}
                      </td>
                      <td data-label="Preis">
                        {readOnly ? formatCurrencyDisplay(row.preis) : (
                          <SuffixInput
                            suffix="€"
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            value={row.preis ?? ""}
                            onChange={input => updateDeliveryNotePosition(activeDeliveryNoteKey, positionIndex, "preis", input.target.value)}
                            onBlur={input => updateDeliveryNotePosition(
                              activeDeliveryNoteKey,
                              positionIndex,
                              "preis",
                              formatCurrencyInput(input.target.value)
                            )}
                          />
                        )}
                      </td>
                      <td data-label="Betrag">{formatCurrencyDisplay(calculateDeliveryLineAmount(row))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="delivery-note-dialog-total">
              <span>Gesamt Lieferschein</span>
              <strong>{formatCurrencyDisplay(total)}</strong>
            </div>
            <div className="delivery-note-attachment">
              <div className="delivery-note-attachment-header">
                <div>
                  <strong>Handschriftlicher Lieferschein</strong>
                  <span>Alle gängigen Bildformate inkl. HEIC/HEIF bis 20 MB</span>
                </div>
                {!readOnly && (
                  <label className="delivery-note-upload-button">
                    Bild {attachment ? "ersetzen" : "hinzufügen"}
                    <input
                      type="file"
                      accept="image/*,.heic,.heif,.avif,.tif,.tiff"
                      capture="environment"
                      onChange={input => handleDeliveryNoteImage(input, activeDeliveryNoteKey)}
                    />
                  </label>
                )}
              </div>
              {attachment ? (
                <figure className="delivery-note-preview">
                  <img src={attachment.imageData} alt={`Lieferschein: ${attachment.fileName || "Beleg"}`} />
                  <figcaption>
                    <span>{attachment.fileName || "Lieferschein"}</span>
                    {!readOnly && (
                      <button type="button" onClick={() => deleteDeliveryNoteImage(activeDeliveryNoteKey)}>
                        <FaTrash /> Bild entfernen
                      </button>
                    )}
                  </figcaption>
                </figure>
              ) : (
                <p className="delivery-note-empty">Noch kein Bild hinterlegt.</p>
              )}
            </div>
          </div>
          <div className="delivery-note-dialog-actions">
            <button type="button" onClick={() => setActiveDeliveryNoteKey(null)}>Fertig</button>
          </div>
        </section>
      </div>
    );
  };

  const renderReadOnlyBillingRows = (positions, type, title, collapseKey = type, rowFilter = () => true) => {
    const rows = (positions || []).filter(pos => pos.position_type === type && rowFilter(pos));
    if (!rows.length) return null;
    const sectionSum = rows.reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);
    const isCollapsed = Boolean(collapsedBillingSections[collapseKey]);
    const isColoredItem = type === "drink" || type === "pfand";
    const isDeliveryNote = type === "custom" && rows.every(pos => pos.custom_type === "delivery_note");

    return (
      <section className={`billing-section ${isCollapsed ? "collapsed" : ""}`}>
        <button
          type="button"
          className="billing-section-toggle"
          onClick={() => toggleBillingSection(collapseKey)}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
          <span>{title}</span>
        </button>
        {!isCollapsed && (
          <table className={`billing-table billing-table-${isDeliveryNote ? "delivery-note" : type}`}>
            <colgroup>
              {isDeliveryNote ? (
                <>
                  <col className="billing-col-label" />
                  <col className="billing-col-count" />
                  <col className="billing-col-money" />
                  <col className="billing-col-money" />
                </>
              ) : type === "custom" ? (
                <>
                  <col className="billing-col-label" />
                  <col className="billing-col-amount" />
                </>
              ) : (
                <>
                  <col className="billing-col-label" />
                  <col className="billing-col-count" />
                  {type !== "voucher" && <col className="billing-col-count" />}
                  <col className="billing-col-money" />
                  <col className="billing-col-money" />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th>{type === "voucher" ? "Marke" : isDeliveryNote ? "Position" : type === "custom" ? "Text" : "Art"}</th>
                {(type !== "custom" || isDeliveryNote) && <th>Anzahl</th>}
                {type !== "voucher" && type !== "custom" && <th>Zurück</th>}
                {(type !== "custom" || isDeliveryNote) && <th>{type === "voucher" ? "Wert" : "Preis"}</th>}
                <th>{isDeliveryNote ? "Betrag" : "Umsatz"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const amount = calculateBillingAmount(row);
                return (
                  <tr key={row.id ?? `${type}-${index}-${row.label}`}>
                    <td
                      className={isColoredItem ? "billing-kind-cell" : undefined}
                      data-label={type === "voucher" ? "Marke" : isDeliveryNote ? "Position" : type === "custom" ? "Text" : "Art"}
                      style={isColoredItem ? getMarkColorStyle(row.farbe) : undefined}
                    >
                      <span className="billing-label">{row.label || "-"}</span>
                    </td>
                    {(type !== "custom" || isDeliveryNote) && <td data-label="Anzahl">{parseWholeNumber(row.anzahl)} Stk.</td>}
                    {type !== "voucher" && type !== "custom" && <td data-label="Zurück">{parseWholeNumber(row.zurueck)} Stk.</td>}
                    {(type !== "custom" || isDeliveryNote) && (
                      <td data-label={type === "voucher" ? "Wert" : "Preis"}>
                        {formatCurrencyDisplay(row.preis)}
                      </td>
                    )}
                    <td data-label={isDeliveryNote ? "Betrag" : "Umsatz"}>{formatCurrencyDisplay(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className="billing-section-total">
          <span>Summe {title}</span>
          <strong>{formatCurrencyDisplay(sectionSum)}</strong>
        </div>
      </section>
    );
  };

  const renderBillingDetail = () => {
    if (!selectedBillingUnit || !billingDetail) return null;
    const total = billingDetail.positions.reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);
    const activeShiftKey = billingDetail.shift?.shift_key || "main";
    const shifts = billingDetail.shifts || [];
    const isAggregateShift = Boolean(billingDetail.isAggregate || billingDetail.shift?.is_total || activeShiftKey === "gesamt");
    const availableShiftMembers = billingDetail.availableMembers || [];
    const canEditShiftSettings = viewerAccountType === "unternehmen" || (
      billingDetail.canEdit && billingDetail.shift?.owner_account_type === "bedienung"
    );
    const canDeleteShift = canEditShiftSettings && !isAggregateShift && shifts.filter(shift => !shift.is_total).length > 1;
    const autosaveText = {
      dirty: "Nicht gespeichert",
      saving: "Speichert...",
      saved: billingAutosaveAt
        ? `Gespeichert ${billingAutosaveAt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`
        : "Gespeichert",
      error: "Speichern fehlgeschlagen",
      idle: "",
    }[billingAutosaveStatus] || "";
    const billingSections = [
      { key: "drink", type: "drink", title: "Getränke" },
      { key: "voucher", type: "voucher", title: "Marken" },
      { key: "custom", type: "custom", title: "Sonstiges", rowFilter: pos => pos.custom_type !== "delivery_note" },
      { key: "pfand", type: "pfand", title: "Pfand" },
    ];

    return (
      <div className="billing-detail">
        <div className="billing-shift-bar">
          <div className="billing-shift-tabs" aria-label="Schichten">
            {shifts.map(shift => (
              <button
                type="button"
                key={shift.shift_key}
                className={`billing-shift-tab ${shift.shift_key === activeShiftKey ? "active" : ""}`}
                onClick={() => loadBillingUnit(selectedBillingUnit, false, selectedDay, shift.shift_key)}
              >
                <span>{shift.shift_name}</span>
                <small>
                  {shift.is_total ? "gesamt" : shift.shift_mode === "included" ? "beachtet" : "privat"}
                  {" · "}
                  {formatCurrencyDisplay(shift.shift_key === activeShiftKey ? total : shift.total || 0)}
                </small>
              </button>
            ))}
          </div>
          <div className="billing-shift-menu">
            <button
              type="button"
              className="billing-settings-button"
              onClick={() => setBillingSettingsOpen(prev => !prev)}
              aria-label="Schichteinstellungen"
            >
              <FaCog />
            </button>
            {billingSettingsOpen && (
              <div className="billing-settings-panel">
                <div className="billing-settings-header">
                  <strong>Schichteinstellungen</strong>
                  <button type="button" onClick={() => setBillingSettingsOpen(false)} aria-label="Schichteinstellungen schließen">
                    <FaTimes />
                  </button>
                </div>
                {isAggregateShift ? (
                  <p>
                    Die Gesamt-Abrechnung addiert alle beachteten Schichten.
                    Wenn du sie speicherst, wird die letzte beachtete Schicht automatisch als Differenz berechnet.
                  </p>
                ) : (
                  <>
                    <label>
                      Schichtname
                      <input
                        type="text"
                        value={shiftNameDraft}
                        onChange={e => setShiftNameDraft(e.target.value)}
                        disabled={!canEditShiftSettings}
                      />
                    </label>
                    <label>
                      Wertung
                      <select
                        value={viewerAccountType === "bedienung" ? "ignored" : shiftModeDraft}
                        onChange={e => setShiftModeDraft(e.target.value)}
                        disabled={viewerAccountType !== "unternehmen"}
                      >
                        <option value="included">Beachtet und in Tagesabrechnung zählen</option>
                        <option value="ignored">Privat / nicht in Tagesabrechnung zählen</option>
                      </select>
                    </label>
                    {viewerAccountType === "unternehmen" && selectedBillingUnit?.unit_type === "team" && (
                      <div className="billing-member-picker">
                        <strong>Gilt für</strong>
                        {availableShiftMembers.map(member => (
                          <label key={member.event_bedienung_id} className="billing-member-option">
                            <input
                              type="checkbox"
                              checked={shiftMemberDraft.map(Number).includes(Number(member.event_bedienung_id))}
                              onChange={() => toggleShiftMemberDraft(member.event_bedienung_id)}
                              disabled={!canEditShiftSettings}
                            />
                            <span>{member.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {viewerAccountType === "bedienung" && (
                      <p>Private Schichten sind nur für dich und zählen nicht zur offiziellen Tagesabrechnung.</p>
                    )}
                  </>
                )}
                <div className="billing-settings-actions">
                  {canEditShiftSettings && !isAggregateShift && (
                    <button type="button" onClick={saveBillingShiftSettings}>
                      <FaCheck /> Speichern
                    </button>
                  )}
                  {canDeleteShift && (
                    <button type="button" className="danger" onClick={deleteBillingShift}>
                      <FaTrash /> Schicht löschen
                    </button>
                  )}
                  <button type="button" onClick={createBillingShift}>
                    <FaPlus /> Schicht hinzufügen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="billing-table-layout">
          <div className="billing-table-stack">
            {renderBillingRows("drink", "Getränke")}
            {renderBillingRows("pfand", "Pfand")}
          </div>
          <div className="billing-table-stack">
            {renderBillingRows("voucher", "Marken")}
            {renderBillingRows("custom", "Sonstiges", pos => pos.custom_type !== "delivery_note")}
            <div className="billing-summary">
              {billingSections.map(section => (
                <div key={section.key} className="billing-summary-row">
                  <span>{section.title}</span>
                  <strong>{formatCurrencyDisplay(getBillingSectionSum(section.type, section.rowFilter))}</strong>
                </div>
              ))}
              <div className="billing-total-bar">
                <span>Abzugeben</span>
                <strong>{formatCurrencyDisplay(total)}</strong>
              </div>
            </div>
          </div>
        </div>
        {renderDeliveryNoteDialog()}
        {renderBedienungPayout()}
        <div className="billing-actions">
          {autosaveText && (
            <span className={`billing-autosave-state ${billingAutosaveStatus}`}>
              {autosaveText}
            </span>
          )}
          <button type="button" className="billing-export-button" onClick={exportBillingPdf}>
            <FaFilePdf /> PDF exportieren
          </button>
          {billingDetail?.canEdit && (
            <button type="button" className="billing-save-button" onClick={saveBillingUnit}>
              Speichern
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTotalDetail = () => {
    const aggregatePositions = dayOverview?.aggregatePositions || [];
    const aggregateSections = [
      { key: "drink", type: "drink", title: "Getränke" },
      { key: "voucher", type: "voucher", title: "Marken" },
      { key: "custom", type: "custom", title: "Sonstiges", rowFilter: pos => pos.custom_type !== "delivery_note" },
      { key: "pfand", type: "pfand", title: "Pfand" },
    ];
    const aggregateSectionSum = (type, rowFilter = () => true) =>
      aggregatePositions
        .filter(pos => pos.position_type === type)
        .filter(rowFilter)
        .reduce((sum, pos) => sum + calculateBillingAmount(pos), 0);

    return (
      <div className="billing-detail">
        <section className="billing-section total-units-section">
          <button type="button" className="billing-section-toggle static" tabIndex={-1}>
            <span>Tagesübersicht</span>
          </button>
          <table className="billing-table">
            <thead>
              <tr>
                <th>Abrechnung</th>
                <th>Typ</th>
                <th>Getränkeumsatz</th>
                <th>Abzugeben</th>
              </tr>
            </thead>
            <tbody>
              {(dayOverview?.units || []).map(unit => (
                <tr key={unit.unit_key}>
                  <td data-label="Abrechnung">{unit.name}</td>
                  <td data-label="Typ">{unit.unit_type === "team" ? "Team" : "Einzel"}</td>
                  <td data-label="Getränkeumsatz">{formatCurrencyDisplay(unit.drinkTotal || 0)}</td>
                  <td data-label="Abzugeben">{formatCurrencyDisplay(unit.payoutTotal ?? unit.total ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="billing-section-total">
            <span>Summe Getränkeumsatz</span>
            <strong>{formatCurrencyDisplay(dayOverview?.drinkTotal || 0)}</strong>
          </div>
          <div className="billing-total-bar">
            <span>Gesamt abzugeben</span>
            <strong>{formatCurrencyDisplay(dayOverview?.payoutTotal ?? dayOverview?.total ?? 0)}</strong>
          </div>
        </section>

        {aggregatePositions.length > 0 ? (
          <div className="billing-table-layout">
            <div className="billing-table-stack">
              {renderReadOnlyBillingRows(aggregatePositions, "drink", "Getränke", "total-drink")}
              {renderReadOnlyBillingRows(aggregatePositions, "pfand", "Pfand", "total-pfand")}
            </div>
            <div className="billing-table-stack">
              {renderReadOnlyBillingRows(aggregatePositions, "voucher", "Marken", "total-voucher")}
              {renderReadOnlyBillingRows(
                aggregatePositions,
                "custom",
                "Sonstiges",
                "total-custom",
                pos => pos.custom_type !== "delivery_note"
              )}
              <div className="billing-summary">
                {aggregateSections.map(section => (
                  <div key={section.key} className="billing-summary-row">
                    <span>{section.title}</span>
                    <strong>{formatCurrencyDisplay(aggregateSectionSum(section.type, section.rowFilter))}</strong>
                  </div>
                ))}
                <div className="billing-total-bar">
                  <span>Abzugeben</span>
                  <strong>{formatCurrencyDisplay(dayOverview?.payoutTotal ?? dayOverview?.total ?? 0)}</strong>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p>Für diesen Tag sind noch keine beachteten Abrechnungen gespeichert.</p>
        )}
      </div>
    );
  };

  const renderDayOverview = () => {
    if (!selectedDay) return null;
    if (billingLoading && !dayOverview && !billingDetail) return <p>Abrechnung wird geladen...</p>;

    if (selectedBillingUnit?.unit_key === "gesamt") return renderTotalDetail();
    if (selectedBillingUnit) return renderBillingDetail();

    const units = dayOverview?.units || [];
    return (
      <div className="day-billing-view">
        <div className="billing-card-grid">
          {viewerAccountType === "unternehmen" && (
            <button
              type="button"
              className="billing-card billing-card-total"
              onClick={async () => {
                if (!(await flushBillingAutosave())) return;
                setSelectedBillingUnit({ unit_key: "gesamt", name: "Gesamt" });
              }}
            >
              <span>Gesamt</span>
              <strong>{formatCurrencyDisplay(dayOverview?.total || 0)}</strong>
              <small>Summe Getränke aller Teams und Einzelbedienungen</small>
            </button>
          )}
          {units.map(unit => (
            <button
              type="button"
              key={unit.unit_key}
              className="billing-card"
              onClick={() => loadBillingUnit(unit)}
            >
              <span>{unit.unit_type === "team" ? "Team" : "Einzel"}</span>
              <strong>{unit.name}</strong>
              <small>
                {unit.hasBilling ? formatCurrencyDisplay(unit.total) : "Noch offen"}
                {unit.ignoredShiftCount > 0 ? ` · ${unit.ignoredShiftCount} privat` : ""}
              </small>
            </button>
          ))}
          {!units.length && (
            <p>
              {viewerAccountType === "bedienung"
                ? "Du bist diesem Tag keiner Abrechnung zugeordnet."
                : "Für diesen Tag sind noch keine Teams oder Einzelbedienungen verfügbar."}
            </p>
          )}
        </div>
      </div>
    );
  };

  const isDayBillingView = tab === "tage" && selectedDay;
  const dayHeaderRight = selectedBillingUnit
    ? selectedBillingUnit.name
    : formatCurrencyDisplay(dayOverview?.total || 0);
  const handleDayHeaderBack = async () => {
    if (selectedBillingUnit) {
      if (!(await flushBillingAutosave())) return;
      if (viewerAccountType === "bedienung") {
        setSelectedDay(null);
        setDayOverview(null);
        setSelectedBillingUnit(null);
        setBillingDetail(null);
        return;
      }
      setSelectedBillingUnit(null);
      setBillingDetail(null);
      return;
    }
    if (!(await flushBillingAutosave())) return;
    setSelectedDay(null);
    setDayOverview(null);
  };

  return (
    <div className="event-detail">
      <div className={`event-detail-header ${isDayBillingView ? "day-mode" : ""}`}>
        {isDayBillingView ? (
          <>
            <div className="header-left">
              <button className="back-button" onClick={handleDayHeaderBack} aria-label="Zurück">
                <FaArrowLeft size={20} />
              </button>
              <h2>{event.name}</h2>
            </div>
            <div className="header-center day-header-center">
              <strong>{formatDate(selectedDay.datum)}</strong>
            </div>
            <div className="header-year">{dayHeaderRight}</div>
          </>
        ) : (
          <>
            <div className="header-left">
              <button className="back-button" onClick={onBack} aria-label="Zurück">
                <FaArrowLeft size={20} />
              </button>
              <h2>{event.name}</h2>
            </div>
            <div className="header-center">
              <span className="event-period">
                {formatDate(event.start_date)}
                {' - '}
                {formatDate(event.end_date)}
              </span>
            </div>
            <div className="header-year">
              <span>{new Date(event.start_date).getFullYear()}</span>
            </div>
          </>
        )}
      </div>
      {!isDayBillingView && (
        <div className="tabs">
          {/* Slider-Pfeil hinter dem aktiven Tab */}
          <div className={`tab-slider ${tab}`}></div>
          {/* Tab-Auswahl */}
          <button
            className={tab === "preislisten" ? "active" : ""}
            onClick={() => setTab("preislisten")}
          >
            Preislisten
          </button>
          <button
            className={tab === "tage" ? "active" : ""}
            onClick={() => setTab("tage")}
          >
            Tage
          </button>
          <button
            className={tab === "bedienungen" ? "active" : ""}
            onClick={() => setTab("bedienungen")}
          >
            Bedienungen
          </button>
        </div>
      )}
      <div className={`tab-content ${isDayBillingView ? "day-billing-content" : ""}`}>
        {tab === "tage" && (
          selectedDay ? (
            renderDayOverview()
          ) : Array.isArray(days) && days.length > 0 ? (
            <div className="tile-grid">
              {days.map((d) => {
                const label = formatDate(d.datum);
                return (
                  <button key={d.id} type="button" className="tile day-tile" onClick={() => loadDayOverview(d)}>
                    <div>
                      <h3>{label}</h3>
                      <span>Tagesabrechnung</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p>Keine Tage verfügbar.</p>
          )
        )}
        {tab === "bedienungen" && (
          <div className="bedienungen-section">
            <div className="teams-tile">
              <h3>Teams</h3>
              <div className="tile-grid">
                {teams.map(team => (
                  <div key={team.id} className="tile">
                    <div className="tile-header">
                      {editingTeamId === team.id ? (
                        <input
                          className="team-edit-input"
                          value={editingTeamName}
                          onChange={e => setEditingTeamName(e.target.value)}
                        />
                      ) : (
                        <h3>{team.name}</h3>
                      )}
                      {editingTeamId === team.id &&
                      <button className="delete-button" onClick={() => handleDeleteTeam(team.id)}>
                        <FaTrash />
                      </button>
                      }
                      <button
                        className="edit-button"
                        onClick={() =>
                          editingTeamId === team.id
                            ? handleSaveTeam(team.id)
                            : handleStartEditTeam(team)
                        }
                      >
                        {editingTeamId === team.id ? <FaCheck /> : <FaPen />}
                      </button>
                    </div>
                    <ul className="team-member-list">
                      {team.members?.length ? team.members.map(member => (
                        <li key={member.assignment_id}>
                          {member.first_name} {member.last_name}
                        </li>
                      )) : (
                        <li className="empty-state">Keine Bedienungen im Team.</li>
                      )}
                    </ul>
                  </div>
                ))}
                
                {accountType === "unternehmen" && (
                  showTeamForm ? (
                    <div className="tile new-team-tile">
                      <input
                        type="text"
                        value={teamNameInput}
                        onChange={e => setTeamNameInput(e.target.value)}
                        placeholder="Team-Name"
                      />
                      <div className="tile-buttons">
                        <button onClick={handleCreateTeam}>Erstellen</button>
                        <button onClick={() => setShowTeamForm(false)}>Abbrechen</button>
                      </div>
                    </div>
                  ) : (
                    <div className="tile add-tile" onClick={() => setShowTeamForm(true)}>                   
                      <span>Neues Team</span>
                      <FaPlus size={24} />
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="bedienungen-tile">
              <h3>Bedienungen</h3>
              <div className="bedienungen-scroll">
                <section className="bedienungen-panel">
                  <h4>Im Event</h4>
                  <ul className="assignments-list">
                    {assignments.length ? assignments.map(a => (
                      <li key={a.assignment_id} className="assignment-row">
                        <div>
                          <strong>{a.first_name} {a.last_name}</strong>
                          <span>{a.source === "manual" ? "Manuell" : "Verbunden"}</span>
                        </div>
                        {accountType === "unternehmen" && (
                          <div className="assignment-actions">
                            <select
                              value={a.team_id || ""}
                              onChange={e => handleTeamAssignmentChange(a.assignment_id, e.target.value)}
                            >
                              <option value="">Einzel</option>
                              {teams.map(team => (
                                <option key={team.id} value={team.id}>{team.name}</option>
                              ))}
                            </select>
                            {a.source === "manual" && (
                              <>
                                <button type="button" onClick={() => handleEditManualWaiter(a)}>
                                  Bearbeiten
                                </button>
                                <select
                                  value=""
                                  onChange={e => handleReplaceManualWaiter(a.assignment_id, e.target.value)}
                                >
                                  <option value="">Verknüpfen</option>
                                  {companyWaiters.map(waiter => (
                                    <option key={waiter.user_id} value={waiter.user_id}>
                                      {waiter.first_name} {waiter.last_name}
                                    </option>
                                  ))}
                                </select>
                              </>
                            )}
                            <button type="button" className="danger-button" onClick={() => handleRemoveAssignment(a)}>
                              Entfernen
                            </button>
                          </div>
                        )}
                      </li>
                    )) : (
                      <li className="empty-state">Noch keine Bedienungen im Event.</li>
                    )}
                  </ul>
                </section>

                {accountType === "unternehmen" && (
                  <>
                    <section className="bedienungen-panel">
                      <h4>Verfügbare Bedienungen</h4>
                      <ul className="available-waiters-list">
                        {availableWaiters.length ? availableWaiters.map(waiter => (
                          <li key={`${waiter.type}-${waiter.user_id || waiter.manual_id}`}>
                            <div>
                              <strong>{waiter.first_name} {waiter.last_name}</strong>
                              <span>{waiter.type === "manual" ? "Manuell" : "Verbunden"}</span>
                            </div>
                            <div className="available-waiter-actions">
                              <button type="button" onClick={() => handleAssignWaiter(waiter)}>
                                Hinzufügen
                              </button>
                              {waiter.type === "manual" && (
                                <button
                                  type="button"
                                  className="danger-button"
                                  onClick={() => handleDeleteAvailableManualWaiter(waiter)}
                                >
                                  Löschen
                                </button>
                              )}
                            </div>
                          </li>
                        )) : (
                          <li className="empty-state">Keine weiteren Bedienungen verfügbar.</li>
                        )}
                      </ul>
                    </section>

                    {showManualForm && (
                      <section className="manual-waiter-form">
                        <input
                          type="text"
                          placeholder="Vorname"
                          value={manualFirstName}
                          onChange={e => setManualFirstName(e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Nachname"
                          value={manualLastName}
                          onChange={e => setManualLastName(e.target.value)}
                        />
                        <div>
                          <button type="button" onClick={handleCreateManualWaiter}>Erstellen</button>
                          <button type="button" onClick={() => setShowManualForm(false)}>Abbrechen</button>
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
              {accountType === "unternehmen" && !showManualForm && (
                <button className="add-button" onClick={() => setShowManualForm(true)}>
                  + Manuelle Bedienung hinzufügen
                </button>
              )}
            </div>
          </div>
        )}
        {tab === "preislisten" && (
          <div className="preislisten-section">
            {accountType === "unternehmen" && (
              <div className="template-toolbar">
                <div className="template-toolbar-actions">
                  <button type="button" onClick={() => setShowTemplatePicker(prev => !prev)}>
                    Vorlage einfügen
                  </button>
                  <button type="button" onClick={handleExportTemplate}>
                    Als Vorlage speichern
                  </button>
                </div>
                {showTemplatePicker && (
                  <div className="template-picker">
                    {templates.length ? templates.map(template => {
                      const hasExistingRows = priceRows.length > 0 || pfandRows.length > 0;
                      return (
                        <div key={template.id} className="template-picker-row">
                          <span>{template.name}</span>
                          <div>
                            <button type="button" onClick={() => handleApplyTemplate(template.id, "replace")}>
                              {hasExistingRows ? "Ersetzen" : "Einfügen"}
                            </button>
                            {hasExistingRows && (
                              <button type="button" onClick={() => handleApplyTemplate(template.id, "append")}>
                                Anhängen
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }) : (
                      <p>Keine Vorlagen vorhanden.</p>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="preislisten-layout">
              <div className="preise-section">
                <div className="preislisten-header">
                  {accountType === "unternehmen" && (
                    <button
                      type="button"
                      className="edit-button preisliste"
                      aria-label={priceEditMode ? "Preisliste speichern" : "Preisliste bearbeiten"}
                      onClick={(e) => {
                        e.preventDefault();
                        if (priceEditMode) {
                          handleSavePriceList();
                        } else {
                          enterPriceEditMode();
                        }
                      }}
                    >
                      {priceEditMode ? <FaCheck size={24} /> : <FaPen size={24} />}
                    </button>
                  )}
                  <h3>Preisliste</h3>
                </div>
                <table className="preislisten-table">
                  <thead>
                    <tr>
                      {priceEditMode && <th className="row-control-header"></th>}
                      <th>Getränkeart</th>
                      <th className="mark-color-column">Markenfarbe</th>
                      <th>Festpreis</th>
                      <th>Bedienpreis</th>
                      {priceEditMode && <th className="row-control-header"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {priceRows.map((row, idx) => {
                      const isPlaceholder = priceEditMode && !hasPriceRowContent(row);
                      return (
                      <tr
                        key={row.id ?? row._draftId ?? idx}
                        className={isPlaceholder ? "placeholder-row" : ""}
                        draggable={priceEditMode && !isPlaceholder}
                        onDragStart={() => !isPlaceholder && setDragPriceIndex(idx)}
                        onDragOver={e => priceEditMode && !isPlaceholder && e.preventDefault()}
                        onDrop={() => !isPlaceholder && handleDropPriceRow(idx)}
                      >
                        {priceEditMode && (
                          <td className="row-control-cell" data-label="Sortieren">
                            {!isPlaceholder && (
                              <button type="button" className="drag-handle" aria-label="Zeile verschieben">
                                <FaGripVertical />
                              </button>
                            )}
                          </td>
                        )}
                        <td
                          className="drink-kind-cell"
                          data-label="Getränkeart"
                          style={getMarkColorStyle(row.markenfarbe)}
                        >
                          {priceEditMode
                            ? <div className="drink-kind-editor">
                                <input
                                  type="text"
                                  placeholder="Getränk"
                                  value={row.getraenkeart}
                                  onKeyDown={handleTableInputKeyDown}
                                  onChange={e => handlePriceRowChange(idx, 'getraenkeart', e.target.value)}
                                />
                                <MarkColorPicker
                                  className="mobile-mark-color-control"
                                  value={row.markenfarbe}
                                  onChange={value => handlePriceRowChange(idx, 'markenfarbe', value)}
                                />
                              </div>
                            : row.getraenkeart
                          }
                        </td>
                        <td className="mark-color-column" data-label="Markenfarbe">
                          {priceEditMode
                            ? <MarkColorPicker
                                value={row.markenfarbe}
                                onChange={value => handlePriceRowChange(idx, 'markenfarbe', value)}
                              />
                            : <MarkColorBadge value={row.markenfarbe} />
                          }
                        </td>
                        <td data-label="Festpreis">
                          {priceEditMode
                            ? <SuffixInput
                                suffix="€"
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                value={row.festpreis}
                                onChange={e => handlePriceRowChange(idx, 'festpreis', e.target.value)}
                                onBlur={() => handlePriceAmountBlur(idx, 'festpreis')}
                              />
                            : formatCurrencyDisplay(row.festpreis)
                          }
                        </td>
                        <td data-label="Bedienpreis">
                          {priceEditMode
                            ? <SuffixInput
                                suffix="€"
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                value={row.bedienpreis}
                                onChange={e => handlePriceRowChange(idx, 'bedienpreis', e.target.value)}
                                onBlur={() => handlePriceAmountBlur(idx, 'bedienpreis')}
                              />
                            : formatCurrencyDisplay(row.bedienpreis)
                          }
                        </td>
                        {priceEditMode && (
                          <td className="row-control-cell" data-label="Aktion">
                            {!isPlaceholder && (
                              <button
                                type="button"
                                className="row-delete-button"
                                aria-label="Eintrag löschen"
                                onClick={() => handleDeletePriceRow(idx)}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="preislisten-side-stack">
            <div className="pfand-section">
              <div className="pfand-header">
                {accountType === "unternehmen" && (
                  <button
                    type="button"
                    className="edit-button"
                    aria-label={pfandEditMode ? "Pfandliste speichern" : "Pfandliste bearbeiten"}
                    onClick={(e) => {
                      e.preventDefault();
                      if (pfandEditMode) {
                        handleSavePfandList();
                      } else {
                        enterPfandEditMode();
                      }
                    }}
                  >
                    {pfandEditMode ? <FaCheck size={24} /> : <FaPen size={24} />}
                  </button>
                )}
                <h3>Pfand</h3>
              </div>
              <table className="pfand-table">
                <thead>
                  <tr>
                    {pfandEditMode && <th className="row-control-header"></th>}
                    <th>Artikel</th>
                    <th className="mark-color-column">Farbe</th>
                    <th>Betrag</th>
                    {pfandEditMode && <th className="row-control-header"></th>}
                  </tr>
                </thead>
                <tbody>
                  {pfandRows.map((row, idx) => {
                    const isPlaceholder = pfandEditMode && !hasPfandRowContent(row);
                    return (
                    <tr
                      key={row.id ?? row._draftId ?? idx}
                      className={isPlaceholder ? "placeholder-row" : ""}
                      draggable={pfandEditMode && !isPlaceholder}
                      onDragStart={() => !isPlaceholder && setDragPfandIndex(idx)}
                      onDragOver={e => pfandEditMode && !isPlaceholder && e.preventDefault()}
                      onDrop={() => !isPlaceholder && handleDropPfandRow(idx)}
                    >
                      {pfandEditMode && (
                        <td className="row-control-cell" data-label="Sortieren">
                          {!isPlaceholder && (
                            <button type="button" className="drag-handle" aria-label="Zeile verschieben">
                              <FaGripVertical />
                            </button>
                          )}
                        </td>
                      )}
                      <td
                        className="drink-kind-cell"
                        data-label="Artikel"
                        style={getMarkColorStyle(row.farbe)}
                      >
                        {pfandEditMode
                          ? <div className="drink-kind-editor">
                              <input
                                type="text"
                                placeholder="Artikel"
                                value={row.artikel}
                                onKeyDown={handleTableInputKeyDown}
                                onChange={e => handlePfandRowChange(idx, 'artikel', e.target.value)}
                              />
                              <MarkColorPicker
                                className="mobile-mark-color-control"
                                value={row.farbe}
                                onChange={value => handlePfandRowChange(idx, 'farbe', value)}
                              />
                            </div>
                          : row.artikel
                        }
                      </td>
                      <td className="mark-color-column" data-label="Farbe">
                        {pfandEditMode
                          ? <MarkColorPicker
                              value={row.farbe}
                              onChange={value => handlePfandRowChange(idx, 'farbe', value)}
                            />
                          : <MarkColorBadge value={row.farbe} />
                        }
                      </td>
                      <td data-label="Betrag">
                        {pfandEditMode
                          ? <SuffixInput
                              suffix="€"
                              type="text"
                              inputMode="decimal"
                              placeholder="0,00"
                              value={row.betrag}
                              onChange={e => handlePfandRowChange(idx, 'betrag', e.target.value)}
                              onBlur={() => handlePfandAmountBlur(idx)}
                            />
                          : formatCurrencyDisplay(row.betrag)
                        }
                      </td>
                      {pfandEditMode && (
                        <td className="row-control-cell" data-label="Aktion">
                          {!isPlaceholder && (
                            <button
                              type="button"
                              className="row-delete-button"
                              aria-label="Eintrag löschen"
                              onClick={() => handleDeletePfandRow(idx)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="marken-section">
              <div className="marken-header">
                {accountType === "unternehmen" && (
                  <button
                    type="button"
                    className="edit-button"
                    aria-label={markenEditMode ? "Markenliste speichern" : "Markenliste bearbeiten"}
                    onClick={(e) => {
                      e.preventDefault();
                      if (markenEditMode) {
                        handleSaveMarkenList();
                      } else {
                        enterMarkenEditMode();
                      }
                    }}
                  >
                    {markenEditMode ? <FaCheck size={24} /> : <FaPen size={24} />}
                  </button>
                )}
                <h3>Markenliste</h3>
              </div>
              <table className="marken-table">
                <thead>
                  <tr>
                    {markenEditMode && <th className="row-control-header"></th>}
                    <th>Marke</th>
                    <th>Wert</th>
                    {markenEditMode && <th className="row-control-header"></th>}
                  </tr>
                </thead>
                <tbody>
                  {markenRows.map((row, idx) => {
                    const isPlaceholder = markenEditMode && !hasMarkenRowContent(row);
                    return (
                      <tr
                        key={row.id ?? row._draftId ?? idx}
                        className={isPlaceholder ? "placeholder-row" : ""}
                        draggable={markenEditMode && !isPlaceholder}
                        onDragStart={() => !isPlaceholder && setDragMarkenIndex(idx)}
                        onDragOver={e => markenEditMode && !isPlaceholder && e.preventDefault()}
                        onDrop={() => !isPlaceholder && handleDropMarkenRow(idx)}
                      >
                        {markenEditMode && (
                          <td className="row-control-cell" data-label="Sortieren">
                            {!isPlaceholder && (
                              <button type="button" className="drag-handle" aria-label="Zeile verschieben">
                                <FaGripVertical />
                              </button>
                            )}
                          </td>
                        )}
                        <td data-label="Marke">
                          {markenEditMode
                            ? <input
                                type="text"
                                placeholder="Marke"
                                value={row.name}
                                onKeyDown={handleTableInputKeyDown}
                                onChange={e => handleMarkenRowChange(idx, 'name', e.target.value)}
                              />
                            : row.name
                          }
                        </td>
                        <td data-label="Wert">
                          {markenEditMode
                            ? <SuffixInput
                                suffix="€"
                                type="text"
                                inputMode="decimal"
                                placeholder="0,00"
                                value={row.wert}
                                onChange={e => handleMarkenRowChange(idx, 'wert', e.target.value)}
                                onBlur={() => handleMarkenAmountBlur(idx)}
                              />
                            : formatCurrencyDisplay(row.wert)
                          }
                        </td>
                        {markenEditMode && (
                          <td className="row-control-cell" data-label="Aktion">
                            {!isPlaceholder && (
                              <button
                                type="button"
                                className="row-delete-button"
                                aria-label="Eintrag löschen"
                                onClick={() => handleDeleteMarkenRow(idx)}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
              </div>
            </div>
          </div>
          )}
        </div>
    </div>
  );
};

export default EventDetail;
